import React from "react"
import { Table } from "react-bootstrap"
import * as Icon from "react-bootstrap-icons"
import { useTranslation } from "react-i18next"
import { LockUnlockIcon } from "@src/components"
import { FleetBadge } from "@src/components/FleetBadge/FleetBadge"
import { colors } from "@src/constants"
import { SLIM_DEVICE_TYPE } from "@src/devices/types"
import { colorTagStyle } from "@src/utils/style"
import { SortArrowIcon } from "../../devicedetail/tabs/components"

const TABLE_COLUMNS = [
  { key: "locked", value: "", sortable: false },
  { key: "serialnumber", value: "serialnumber", sortable: true },
  { key: "name", value: "device_name", sortable: true },
  { key: "id_number", value: "id_number", sortable: true },
  { key: "battery", value: "batterie_status", sortable: true },
  { key: "servicebooking", value: "active_service", sortable: false },
  { key: "last_position_timestamp", value: "last_position", sortable: true },
  { key: "last_seen_timestamp", value: "last_seen", sortable: true },
] as const

const NOWRAP: React.CSSProperties = { whiteSpace: "nowrap" }

export type DevicesTableProps = {
  devices: SLIM_DEVICE_TYPE[]
  sorting: boolean
  sortHeader: string
  headerLoad: boolean
  onClickTableHeader: (key: string) => void
  onRowClick: (item: SLIM_DEVICE_TYPE, event: MouseEvent) => void
  formatDateTime: (date: Date) => string
}

export const DevicesTable: React.FC<DevicesTableProps> = ({
  devices,
  sorting,
  sortHeader,
  headerLoad,
  onClickTableHeader,
  onRowClick,
  formatDateTime,
}) => {
  const { t } = useTranslation()

  return (
    <Table hover responsive>
      <thead>
        <tr style={NOWRAP}>
          {TABLE_COLUMNS.map(({ key, value, sortable }) => (
            <th
              key={key}
              className={sortable ? "pointer" : ""}
              onClick={sortable ? () => onClickTableHeader(key) : undefined}
            >
              {value && (
                <div className="d-flex flex-row align-items-center">
                  {t(`devices_screen.${value}`)}
                  {sortable && sortHeader == key && (
                    <SortArrowIcon
                      loading={headerLoad}
                      arrowUp={
                        ["last_seen_timestamp"].includes(key) ||
                        ["last_position_timestamp"].includes(key)
                          ? !sorting
                          : sorting
                      }
                    />
                  )}
                </div>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {devices &&
          devices.map((device, i) => (
            <tr
              key={i}
              onClick={(event) => onRowClick(device, event as unknown as MouseEvent)}
              style={NOWRAP}
              className="pointer"
            >
              <td>
                {!device.reported_theft ? (
                  <LockUnlockIcon locked={device.locked} />
                ) : (
                  <Icon.Flag style={colorTagStyle(colors.red)} />
                )}
              </td>
              <td>
                <div className="d-flex flex-row justify-content-between">
                  <div>{device.serialnumber}</div>
                  <div>
                    {device.powersaving_ends_at && (
                      <Icon.LightningChargeFill color={colors.mustardYellow} />
                    )}
                  </div>
                </div>
              </td>
              <td>
                <div className="d-flex flex-row align-items-center gap-2">
                  <span>{device.name}</span>
                  {device?.fleet?.name && (
                    <FleetBadge fleetName={device.fleet.name} className="ms-1" />
                  )}
                </div>
              </td>
              <td>{device.id_number || "-"}</td>
              <td>
                <div className="d-flex align-items-center justify-content-center">
                  <label>{device.battery} %</label>
                  {device.charging && (
                    <Icon.BatteryCharging className="ms-1" color={colors.green} size={20} />
                  )}
                </div>
              </td>
              <td>
                {device?.servicebooking
                  ? formatDateTime(new Date(device.servicebooking.service_end_date))
                  : "-"}
              </td>
              <td>
                {device?.last_position_timestamp
                  ? formatDateTime(new Date(device.last_position_timestamp))
                  : "-"}
              </td>
              <td>
                {device?.last_seen_timestamp
                  ? formatDateTime(new Date(device.last_seen_timestamp))
                  : "-"}
              </td>
            </tr>
          ))}
      </tbody>
    </Table>
  )
}
